import React, { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, Select } from "@material-ui/core";
import FilterListIcon from "@material-ui/icons/FilterList";
import { SortDirectionEnum } from "types/constants";
import { CompanyLevel, UserProfile } from "types";
import { UsersAutocomplete } from "components/UsersAutocomplete";
import { TuneIcon } from "svgComponents";
import usePrevious from "hooks/usePrevious";
import { isHighLevel } from "helpers/companyHelper";
import { getUsersByManagerId } from "services/userServices";
import useLocationStateParam from "hooks/useLocationStateParam";

import useStyles from "./styles";

export interface FiltersData<S> {
    sortDirection: SortDirectionEnum;
    status: S | "";
    memberId: string | null;
    leaderId: string | null;
}

interface FiltersProps<S> {
    currentUser?: UserProfile;
    currentTeam?: UserProfile[];
    companyLevels?: CompanyLevel[];
    membersBelow: UserProfile[];
    coachesBelow?: UserProfile[];
    loading: boolean;
    statusOptions?: { value: S, label: string }[];
    onFiltersChange: (filters: FiltersData<S>) => void;
}

const TuneFilterIcon = (props: unknown) => <TuneIcon height={24} {...props} />;

function CoachingSessionFilters<S extends string>({
    currentUser,
    currentTeam,
    companyLevels,
    membersBelow,
    coachesBelow,
    loading,
    statusOptions,
    onFiltersChange,
}: FiltersProps<S>) {
    const classes = useStyles();
    const prevOnFiltersChange = usePrevious(onFiltersChange);
    const sessionIdStateParam = useLocationStateParam("sessionId");

    const isHighLevelUser = currentUser && companyLevels && isHighLevel(currentUser.level, companyLevels);

    const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.desc);
    const [status, setStatus] = useState<S | "">("");
    const [memberId, setMemberId] = useState<string>("");
    const [leaderId, setLeaderId] = useState<string>(isHighLevelUser && currentUser && !sessionIdStateParam ? currentUser.id : "");

    const [membersByLeaders, setMembersByLeaders] = useState<{ [key: string]: UserProfile[] }>(
        isHighLevelUser && currentUser && currentTeam ? { [currentUser.id]: currentTeam } : {}
    );

    const coaches = useMemo(() => [...(coachesBelow ?? []), ...(currentUser ? [currentUser] : [])], [coachesBelow, currentUser]);
    const members = useMemo(() => leaderId ? (membersByLeaders[leaderId] ?? []) : membersBelow, [leaderId, membersByLeaders, membersBelow]);

    useEffect(() => {
        if (onFiltersChange !== prevOnFiltersChange) {
            onFiltersChange({ sortDirection, status, memberId, leaderId });
        }
    }, [onFiltersChange, prevOnFiltersChange, sortDirection, status, memberId, leaderId]);

    const handleSortDirectionChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        const sortDirection = e.target.value as SortDirectionEnum;
        setSortDirection(sortDirection);
        onFiltersChange({ sortDirection, status, memberId, leaderId });
    };

    const handleStatusChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        const status = e.target.value as S;
        setStatus(status);
        onFiltersChange({ sortDirection, status, memberId, leaderId });
    };

    const handleLeaderChange = (leaderId: string | null) => {
        setLeaderId(leaderId ?? "");
        if (leaderId) setMemberId("");

        if (leaderId && !membersByLeaders[leaderId]) {
            getUsersByManagerId(leaderId).then((users) => {
                setMembersByLeaders({ ...membersByLeaders, [leaderId]: users });
            });
        }

        onFiltersChange({ sortDirection, status, memberId: leaderId ? "" : memberId, leaderId });
    };

    const handleMemberChange = (memberId: string | null) => {
        setMemberId(memberId ?? "");
        onFiltersChange({ sortDirection, status, memberId, leaderId });
    };

    return (
        <Box className={classes.filtersContainer}>
            {isHighLevelUser && (
                <Box minWidth={220}>
                    <UsersAutocomplete
                        label="Team Leader"
                        allUsersOption="All Team Leaders"
                        userProfiles={coaches}
                        popupIcon={<FilterListIcon />}
                        value={leaderId}
                        onChange={handleLeaderChange}
                        disabled={loading}
                    />
                </Box>
            )}
            <Box minWidth={220}>
                <UsersAutocomplete
                    label="Team Member"
                    allUsersOption="All Team Members"
                    userProfiles={members}
                    popupIcon={<FilterListIcon />}
                    value={memberId}
                    onChange={handleMemberChange}
                    disabled={loading}
                    loading={!!leaderId && !membersByLeaders[leaderId]}
                />
            </Box>
            {statusOptions && (
                <Select
                    variant="outlined"
                    value={status}
                    onChange={handleStatusChange}
                    IconComponent={FilterListIcon}
                    disabled={loading}
                    displayEmpty
                    className={classes.filter}
                >
                    <MenuItem value="">
                        All Statuses
                    </MenuItem>
                    {statusOptions.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                            {label}
                        </MenuItem>
                    ))}
                </Select>
            )}
            <Select
                variant="outlined"
                value={sortDirection}
                onChange={handleSortDirectionChange}
                IconComponent={TuneFilterIcon}
                disabled={loading}
                className={classes.filter}
            >
                <MenuItem value={SortDirectionEnum.desc}>
                    Most Recent
                </MenuItem>
                <MenuItem value={SortDirectionEnum.asc}>
                    Oldest
                </MenuItem>
            </Select>
        </Box>
    );
}

export default React.memo(CoachingSessionFilters) as typeof CoachingSessionFilters;
